import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useWODraws, useWODrawLines } from "@/hooks/useWODraws";
import { usePermissions } from "@/hooks/usePermissions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MoreVertical, Eye, CheckCircle, DollarSign, Ban } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  wo: any;
  onMutate: () => void;
}

const statusBadge = (status: string) => {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
    draft: { label: "Draft", variant: "outline" },
    submitted: { label: "⏳ Submitted", variant: "secondary" },
    approved: { label: "⏳ Approved", variant: "default" },
    paid: { label: "★ Paid", variant: "default" },
    voided: { label: "Voided", variant: "destructive" },
  };
  const m = map[status] ?? { label: status, variant: "outline" as const };
  return <Badge variant={m.variant} className="text-[10px]">{m.label}</Badge>;
};

export const WODrawHistory = ({ wo, onMutate }: Props) => {
  const { data: draws, refetch } = useWODraws(wo.id);
  const { can, isAdmin } = usePermissions();
  const canManage = isAdmin || can("approve_bills");
  const [viewDrawId, setViewDrawId] = useState<string | null>(null);

  const totalBilled = (draws ?? []).reduce((s: number, d: any) => s + Number(d.subtotal ?? 0), 0);
  const totalRetainage = (draws ?? []).reduce((s: number, d: any) => s + Number(d.retainage_amount ?? 0), 0);
  const totalPaid = (draws ?? []).filter((d: any) => d.status === "paid").reduce((s: number, d: any) => s + Number(d.net_due ?? 0), 0);

  const handleStatusChange = async (drawId: string, newStatus: string) => {
    try {
      const updates: any = { status: newStatus };
      if (newStatus === "paid") updates.paid_at = new Date().toISOString();
      if (newStatus === "approved") {
        updates.approved_at = new Date().toISOString();
        updates.approved_by = (await supabase.auth.getUser()).data.user?.id;
      }

      const { error } = await supabase.from("wo_draws" as any).update(updates).eq("id", drawId);
      if (error) throw error;

      // If marking paid, update WO lines to paid
      if (newStatus === "paid") {
        const { data: drawLines } = await supabase.from("wo_draw_lines" as any).select("work_order_line_id").eq("draw_id", drawId);
        if (drawLines) {
          for (const dl of drawLines as any[]) {
            await supabase.from("work_order_lines").update({ status: "paid" } as any).eq("id", dl.work_order_line_id);
          }
        }
      }

      const { data: drawCompanyId } = await supabase.rpc("my_company_id");
      await supabase.from("ops_audit_log").insert({
        company_id: drawCompanyId,
        table_name: "wo_draws",
        record_id: drawId,
        action: `draw_${newStatus}`,
        new_status: newStatus,
        changed_by: (await supabase.auth.getUser()).data.user?.id,
        metadata: { wo_number: wo.number },
      } as any);

      toast.success(`Draw marked as ${newStatus}`);
      refetch();
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (!(draws ?? []).length) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No draws yet.</p>;
  }

  return (
    <>
      <div className="space-y-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Draw</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Lines</TableHead>
              <TableHead className="text-right">Subtotal</TableHead>
              <TableHead className="text-right">Retainage</TableHead>
              <TableHead className="text-right">Net Due</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(draws ?? []).map((draw: any) => (
              <TableRow key={draw.id}>
                <TableCell className="font-mono font-medium">Draw #{draw.draw_number}</TableCell>
                <TableCell className="text-sm">{draw.draw_date ? format(new Date(draw.draw_date + "T00:00:00"), "MMM d") : "—"}</TableCell>
                <TableCell className="text-right text-sm">—</TableCell>
                <TableCell className="text-right text-sm">{fmt(Number(draw.subtotal))}</TableCell>
                <TableCell className="text-right text-sm text-muted-foreground">-{fmt(Number(draw.retainage_amount))}</TableCell>
                <TableCell className="text-right text-sm font-medium">{fmt(Number(draw.net_due))}</TableCell>
                <TableCell>{statusBadge(draw.status)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewDrawId(draw.id)}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    {canManage && draw.status !== "paid" && draw.status !== "voided" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <MoreVertical className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {draw.status === "draft" && (
                            <DropdownMenuItem onClick={() => handleStatusChange(draw.id, "approved")}>
                              <CheckCircle className="h-3.5 w-3.5 mr-2" /> Approve
                            </DropdownMenuItem>
                          )}
                          {draw.status === "approved" && (
                            <DropdownMenuItem onClick={() => handleStatusChange(draw.id, "paid")}>
                              <DollarSign className="h-3.5 w-3.5 mr-2" /> Mark Paid
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {/* Totals row */}
            <TableRow className="bg-muted/30 font-medium">
              <TableCell colSpan={3} className="text-right text-sm">Totals</TableCell>
              <TableCell className="text-right text-sm">{fmt(totalBilled)}</TableCell>
              <TableCell className="text-right text-sm text-muted-foreground">{fmt(totalRetainage)}</TableCell>
              <TableCell className="text-right text-sm">{fmt(totalBilled - totalRetainage)}</TableCell>
              <TableCell className="text-xs text-muted-foreground">Paid: {fmt(totalPaid)}</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* View Draw Detail */}
      {viewDrawId && (
        <DrawDetailDialog
          drawId={viewDrawId}
          wo={wo}
          onClose={() => setViewDrawId(null)}
        />
      )}
    </>
  );
};

// Draw detail dialog showing the snapshot of billed lines
function DrawDetailDialog({ drawId, wo, onClose }: { drawId: string; wo: any; onClose: () => void }) {
  const { data: drawLines } = useWODrawLines(drawId);
  const { data: draws } = useWODraws(wo.id);
  const draw = (draws ?? []).find((d: any) => d.id === drawId);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Draw #{draw?.draw_number} — {wo.number}</DialogTitle>
        </DialogHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead>UOM</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(drawLines ?? []).map((dl: any) => (
              <TableRow key={dl.id}>
                <TableCell className="text-sm">{dl.description}</TableCell>
                <TableCell className="text-right text-sm">{Number(dl.quantity).toLocaleString()}</TableCell>
                <TableCell className="text-sm">{dl.unit_of_measure}</TableCell>
                <TableCell className="text-right text-sm">{fmt(Number(dl.contract_rate))}</TableCell>
                <TableCell className="text-right text-sm font-medium">{fmt(Number(dl.line_total))}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/30 font-medium">
              <TableCell colSpan={4} className="text-right">Subtotal</TableCell>
              <TableCell className="text-right">{fmt(Number(draw?.subtotal ?? 0))}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} className="text-right text-muted-foreground">Retainage ({draw?.retainage_pct}%)</TableCell>
              <TableCell className="text-right text-muted-foreground">-{fmt(Number(draw?.retainage_amount ?? 0))}</TableCell>
            </TableRow>
            <TableRow className="font-semibold">
              <TableCell colSpan={4} className="text-right">Net Due</TableCell>
              <TableCell className="text-right">{fmt(Number(draw?.net_due ?? 0))}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}
